import type { RefundDetail, RefundView } from '@gatecheckpro/contracts';
import { REFUND_STATES, Status, formatMoney, presentation } from '@gatecheckpro/design-system';

/**
 * A refund, as the person deciding about it reads it.
 *
 * The state this screen has to get right is the awkward one. Between `APPROVED`
 * and `SUCCEEDED` the customer's tickets are already void and their money has
 * not arrived — the single most complained-about window in any refund system.
 * Drawing approval as a completed, positive thing would hide exactly the period
 * somebody is going to be asked about, so it is drawn as in-progress and its
 * wording says the money has not gone back yet.
 *
 * `FAILED` gets the same treatment from the other side. It is not an ending: the
 * payout did not happen, the tickets stay void, and a person has to act. A
 * surface that drew it as a closed state would leave a customer with neither
 * ticket nor money and nobody looking at it.
 *
 * Nothing here decides anything. Whether a refund may be requested, approved or
 * retried is the server's answer; this file renders the answer it was given.
 */

/** Whether the money is known to have gone back. Only one state means that. */
export function moneyHasGoneBack(refund: RefundView): boolean {
  return refund.status === 'SUCCEEDED';
}

/**
 * Whether the tickets are void because of this refund.
 *
 * True from approval onward, including `FAILED` — a failed payout does not give
 * the ticket back, and a screen implying otherwise would send somebody to a gate
 * with a pass that will be refused.
 */
export function ticketsAreVoid(refund: RefundView): boolean {
  return (
    refund.status === 'APPROVED' ||
    refund.status === 'PROVIDER_PENDING' ||
    refund.status === 'SUCCEEDED' ||
    refund.status === 'FAILED' ||
    refund.status === 'REVIEW_REQUIRED'
  );
}

export function RefundStatusPill({ refund }: { refund: RefundView }): React.JSX.Element {
  const state = presentation(REFUND_STATES, refund.status);
  return <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />;
}

export function RefundSummary({ refund }: { refund: RefundView }): React.JSX.Element {
  const state = presentation(REFUND_STATES, refund.status);
  return (
    <div className="gc-stack gc-stack--tight" data-testid={`refund-${refund.id}`}>
      <div className="gc-row gc-row--between gc-row--baseline">
        <strong>{refund.refundNumber}</strong>
        <RefundStatusPill refund={refund} />
      </div>
      <span className="gc-mono">{formatMoney(refund.amount)}</span>
      <span className="gc-muted">{state.meaning}</span>
      <span className="gc-muted">
        Order {refund.orderNumber} · {refund.tickets.length}{' '}
        {refund.tickets.length === 1 ? 'ticket' : 'tickets'}
      </span>
      {/*
        The reason, as recorded. Free text is the organizer's own words and is
        shown as written rather than summarised — finance groups by the
        category, and a person reading one case needs the story.
      */}
      <span className="gc-muted">
        {refund.reasonCategory.replaceAll('_', ' ').toLowerCase()}
        {refund.reasonDetail === null ? '' : ` — ${refund.reasonDetail}`}
      </span>
      {ticketsAreVoid(refund) && !moneyHasGoneBack(refund) && (
        <span className="gc-muted" data-testid={`refund-gap-${refund.id}`}>
          The tickets are already void. The money has not gone back yet.
        </span>
      )}
    </div>
  );
}

/**
 * Everything that happened, in order.
 *
 * Rendered from the server's append-only history rather than reconstructed from
 * the current status and its timestamps. A surface that inferred "it must have
 * been approved before it was sent" would be writing history rather than
 * reporting it, and would be wrong the first time a refund took a path nobody
 * anticipated.
 */
export function RefundHistory({ refund }: { refund: RefundDetail }): React.JSX.Element {
  return (
    <ol className="gc-stack gc-stack--tight" data-testid={`refund-history-${refund.id}`}>
      {refund.history.map((entry) => (
        <li key={`${entry.occurredAt}-${entry.toStatus}`}>
          <span className="gc-mono gc-muted">{new Date(entry.occurredAt).toLocaleString()}</span>{' '}
          <strong>{presentation(REFUND_STATES, entry.toStatus).label}</strong>{' '}
          <span className="gc-muted">{entry.reasonCode.replaceAll('_', ' ').toLowerCase()}</span>
        </li>
      ))}
    </ol>
  );
}
